import { Chart as G2Chart } from '@antv/g2';
import { forwardRef, HTMLAttributes, Ref, useEffect, useImperativeHandle, useRef } from 'react';

import { IBaseChartProps, IComponent } from '../../../models/components.models';
import { useClassNames } from '../../../utils/hooks';
import { IRadialChartClassNames, IRadialChartStyles, radialChartStylesDefault } from './RadialChart.styles';

const render = (
  el: HTMLElement,
  data: { [key: string]: any }[],
  labelFieldName: string,
  valueFieldNames: string[],
  colors: string[] = ['#BAE7FF', '#1890FF', '#0050B3']
) => {
  const chart = new G2Chart({ container: el, autoFit: true });
  chart.coordinate({ type: 'radial', innerRadius: 0.1, endAngle: Math.PI });
  chart
    .interval()
    .data(data)
    .encode('x', labelFieldName)
    .encode('y', valueFieldNames[0])
    .encode('color', valueFieldNames[0])
    .style('stroke', 'white')
    .scale('color', { range: colors.join('-') })
    .axis('y', { tickFilter: (_d: any, i: number) => i !== 0 })
    .legend({
      color: {
        length: 400,
        position: 'bottom',
        layout: { justifyContent: 'center' },
      },
    })
    .animate('enter', { type: 'waveIn', duration: 800 });
  chart.render();
};

export interface IRadialChartProps
  extends IComponent<IRadialChartStyles>,
    HTMLAttributes<HTMLDivElement>,
    IBaseChartProps {
  colors?: string[];
}

export const RadialChart = forwardRef((props: IRadialChartProps, ref: Ref<HTMLDivElement>) => {
  // some variables are not used, but they need to get `elementProps`.
  const { className, styles, data = [], colors, labelFieldName = '', valueFieldNames = [], ...elementProps } = props;
  const refRoot = useRef<HTMLDivElement>(null);
  useImperativeHandle(ref, () => refRoot.current as HTMLDivElement);
  useEffect(() => {
    if (refRoot.current) {
      render(refRoot.current, data, labelFieldName, valueFieldNames, colors);
    }
  }, [data, labelFieldName, valueFieldNames, colors]);

  const classNames = useClassNames<Partial<IRadialChartStyles>, IRadialChartClassNames>(
    'RadialChart',
    radialChartStylesDefault,
    styles,
    className
  );
  return <div {...elementProps} ref={refRoot} className={classNames.root} />;
});
